import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts';
import { api } from "../../lib/api";

// Stat Card Component
const StatCard = ({ title, value, icon, color, highlight }) => {
  const colorClasses = {
    blue: 'text-blue-500 bg-blue-100 dark:bg-blue-900/30',
    purple: 'text-purple-500 bg-purple-100 dark:bg-purple-900/30',
    green: 'text-emerald-500 bg-emerald-100 dark:bg-emerald-900/30',
    red: 'text-red-500 bg-red-100 dark:bg-red-900/30',
    amber: 'text-amber-500 bg-amber-100 dark:bg-amber-900/30',
  };

  return (
    <div className={`stat-card ${highlight ? 'ring-2 ring-red-500' : ''}`}>
      <div className="flex items-center justify-between">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
          </svg>
        </div>
      </div>
      <div className="mt-4">
        <p className="text-3xl font-bold font-['Plus_Jakarta_Sans']">{value}</p>
        <p className="text-sm text-muted-foreground">{title}</p>
      </div>
    </div>
  );
};

// Real-Time Alerts Panel Component
const RealTimeAlertsPanel = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await api.get('/alerts/realtime');
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const dismissAlert = async (alertId) => {
    try {
      await api.put(`/alerts/${alertId}/dismiss`);
      setAlerts(alerts.filter(a => a.alert_id !== alertId));
      toast.success('Alerta arquivado');
    } catch (error) {
      console.error('Error dismissing alert:', error);
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'critical_conflict':
        return (
          <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/30">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
        );
      case 'offer_limit_exceeded':
        return (
          <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900/30">
            <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case 'offer_auto_rejected':
        return (
          <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/30">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/30">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
    }
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      critical: 'bg-red-500 text-white',
      high: 'bg-amber-500 text-white',
      medium: 'bg-yellow-400 text-black',
      low: 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
    };
    return colors[priority] || colors.low;
  };

  const criticalCount = alerts.filter(a => a.priority === 'critical' || a.priority === 'high').length;

  return (
    <div className="rounded-xl border bg-card overflow-hidden" data-testid="realtime-alerts-panel">
      <div 
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${criticalCount > 0 ? 'bg-red-100 dark:bg-red-900/30 animate-pulse' : 'bg-blue-100 dark:bg-blue-900/30'}`}>
            <svg className={`w-5 h-5 ${criticalCount > 0 ? 'text-red-600' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold font-['Plus_Jakarta_Sans'] flex items-center gap-2">
              Alertas em Tempo Real
              {alerts.length > 0 && (
                <span className="px-2 py-0.5 rounded-full text-xs bg-red-500 text-white">
                  {alerts.length}
                </span>
              )}
            </h2>
            <p className="text-sm text-muted-foreground">
              {criticalCount > 0 
                ? `${criticalCount} alerta(s) crítico(s) requerem atenção` 
                : 'Monitorização de conflitos e ofertas'}
            </p>
          </div>
        </div>
        <svg 
          className={`w-5 h-5 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {expanded && (
        <div className="border-t border-border">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : alerts.length === 0 ? (
            <div className="text-center py-8">
              <svg className="w-12 h-12 mx-auto text-emerald-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-muted-foreground">Sem alertas ativos</p>
              <p className="text-sm text-muted-foreground">O sistema está a monitorizar conflitos e ofertas</p>
            </div>
          ) : (
            <div className="divide-y divide-border max-h-96 overflow-y-auto">
              {alerts.map((alert) => (
                <div 
                  key={alert.alert_id} 
                  className="p-4 hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    {getAlertIcon(alert.type)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityBadge(alert.priority)}`}>
                          {alert.priority === 'critical' ? 'CRÍTICO' : 
                           alert.priority === 'high' ? 'ALTO' : 
                           alert.priority === 'medium' ? 'MÉDIO' : 'BAIXO'}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(alert.created_at).toLocaleString('pt-PT')}
                        </span>
                      </div>
                      <p className="font-medium text-sm">{alert.title}</p>
                      <p className="text-sm text-muted-foreground mt-1">{alert.message}</p>
                      <div className="flex items-center gap-2 mt-2">
                        {alert.entity_type && (
                          <button
                            onClick={() => navigate(`/${alert.entity_type}s/${alert.entity_id}`)}
                            className="text-xs text-primary hover:underline"
                          >
                            Ver {alert.entity_type === 'offer' ? 'oferta' : alert.entity_type === 'conflict' ? 'conflito' : 'entidade'}
                          </button>
                        )}
                        <button
                          onClick={() => dismissAlert(alert.alert_id)}
                          className="text-xs text-muted-foreground hover:text-foreground"
                        >
                          Arquivar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="p-3 border-t border-border bg-muted/30">
            <button
              onClick={fetchAlerts}
              className="w-full py-2 text-sm text-primary hover:bg-muted rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Atualizar Alertas
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Dashboard Detail Modal
const DashboardDetailModal = ({ type, data, onClose, onNavigate }) => {
  const navigate = useNavigate();
  const typeLabels = {
    clients: 'Clientes',
    suppliers: 'Fornecedores',
    employees: 'Colaboradores',
    offerer: 'Ofertante',
    exceeds_limit: 'Ofertas que Excedem Limite',
    auto_rejected: 'Ofertas Auto-Rejeitadas',
    pending: 'Ofertas Pendentes de Aprovação'
  };

  const statusLabels = {
    pending: 'Pendente',
    approved: 'Aprovada',
    rejected: 'Rejeitada',
    auto_rejected: 'Auto-Rejeitada',
    ipss_donated: 'Doada IPSS',
    lottery: 'Sorteio'
  };

  const isOfferList = ['exceeds_limit', 'auto_rejected', 'pending'].includes(type);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl border border-border w-full max-w-2xl max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="text-xl font-semibold font-['Plus_Jakarta_Sans']">
            {typeLabels[type] || type}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {isOfferList ? (
            <>
              {(!data || data.length === 0) ? (
                <p className="text-center text-muted-foreground py-8">Nenhuma oferta encontrada</p>
              ) : (
                <div className="space-y-3">
                  {data.map((offer, idx) => (
                    <div
                      key={offer.offer_id || idx}
                      className="p-4 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => { navigate('/offers'); onClose(); }}
                      data-testid={`offer-detail-${idx}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium">{offer.employee_name}</p>
                          <p className="text-sm text-muted-foreground">Oferecido por: {offer.offered_by}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg">{offer.value?.toLocaleString('pt-PT')} €</p>
                          {offer.limit_exceeded_amount > 0 && (
                            <p className="text-xs text-red-500">+{offer.limit_exceeded_amount?.toFixed(2)}€ acima</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{offer.offer_date}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          offer.status === 'pending' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30' :
                          offer.status === 'approved' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30' :
                          'bg-red-100 text-red-700 dark:bg-red-900/30'
                        }`}>
                          {statusLabels[offer.status] || offer.status}
                        </span>
                      </div>
                      {offer.exceeds_limit && (
                        <div className="mt-2 text-xs text-orange-500 flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          Excede limite da política
                        </div>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => { navigate('/offers'); onClose(); }}
                    className="w-full py-2 text-sm text-primary hover:bg-muted rounded-lg transition-colors mt-4"
                  >
                    Ver todas as ofertas →
                  </button>
                </div>
              )}
            </>
          ) : type === 'offerer' ? (
            <>
              <div className="text-center pb-4 border-b border-border">
                <h3 className="text-lg font-bold">{data?.name}</h3>
                <p className="text-sm text-muted-foreground capitalize">{data?.type === 'client' ? 'Cliente' : data?.type === 'supplier' ? 'Fornecedor' : 'Outro'}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-muted/50 text-center">
                  <p className="text-2xl font-bold">{data?.count || 0}</p>
                  <p className="text-sm text-muted-foreground">Total Ofertas</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50 text-center">
                  <p className="text-2xl font-bold text-emerald-500">{data?.value?.toLocaleString('pt-PT') || 0} €</p>
                  <p className="text-sm text-muted-foreground">Valor Total</p>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Risk Distribution */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-lg bg-red-100 dark:bg-red-900/30 text-center">
                  <p className="text-xl font-bold text-red-500">{data?.high_risk || 0}</p>
                  <p className="text-xs text-muted-foreground">Alto Risco</p>
                </div>
                <div className="p-3 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-center">
                  <p className="text-xl font-bold text-amber-500">{data?.medium_risk || 0}</p>
                  <p className="text-xs text-muted-foreground">Médio Risco</p>
                </div>
                <div className="p-3 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-center">
                  <p className="text-xl font-bold text-emerald-500">{data?.low_risk || 0}</p>
                  <p className="text-xs text-muted-foreground">Baixo Risco</p>
                </div>
              </div>

              {/* Status Distribution */}
              <div className="pt-4 border-t border-border">
                <h4 className="text-sm font-medium mb-3">Por Status</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-2 rounded-lg bg-muted/50 text-center">
                    <p className="font-bold">{data?.by_status?.pending || 0}</p>
                    <p className="text-xs text-muted-foreground">Pendente</p>
                  </div>
                  <div className="p-2 rounded-lg bg-muted/50 text-center">
                    <p className="font-bold text-emerald-500">{data?.by_status?.approved || 0}</p>
                    <p className="text-xs text-muted-foreground">Aprovado</p>
                  </div>
                  <div className="p-2 rounded-lg bg-muted/50 text-center">
                    <p className="font-bold text-red-500">{data?.by_status?.rejected || 0}</p>
                    <p className="text-xs text-muted-foreground">Rejeitado</p>
                  </div>
                </div>
              </div>

              {/* High Risk Entities Preview */}
              {data?.high_risk_entities?.length > 0 && (
                <div className="pt-4 border-t border-border">
                  <h4 className="text-sm font-medium mb-3">Entidades de Alto Risco</h4>
                  <div className="space-y-2">
                    {data.high_risk_entities.map((entity, idx) => {
                      const entityType = type.slice(0, -1);
                      const idKey = `${entityType}_id`;
                      return (
                        <div 
                          key={idx}
                          className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted cursor-pointer transition-colors"
                          onClick={() => onNavigate(type, entity[idKey])}
                        >
                          <div>
                            <p className="font-medium">{entity.name}</p>
                            <p className="text-xs text-muted-foreground">Score: {Math.round((entity.risk_score || 0) * 100)}</p>
                          </div>
                          <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Navigate Button */}
          {type !== 'offerer' && (
            <button
              onClick={() => onNavigate(type, '')}
              className="w-full mt-4 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
            >
              Ver Todos os {typeLabels[type]}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Dashboard Component
const DashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [offerAnalytics, setOfferAnalytics] = useState(null);
  const [entityStats, setEntityStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailModal, setDetailModal] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, analyticsRes, entityRes] = await Promise.all([
          api.get('/dashboard/stats'),
          api.get('/dashboard/offer-analytics'),
          api.get('/dashboard/entity-stats')
        ]);
        setStats(statsRes.data);
        setOfferAnalytics(analyticsRes.data);
        setEntityStats(entityRes.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast.error('Erro ao carregar dados do dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const CHART_COLORS = ['#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#3b82f6'];
  const TYPE_LABELS = { gift: 'Presente', invitation: 'Convite', benefit: 'Benefício', sponsorship: 'Patrocínio', other: 'Outro' };
  const SOURCE_LABELS = { client: 'Cliente', supplier: 'Fornecedor', other: 'Outro' };

  const handleCardClick = (type, data) => {
    setDetailModal({ type, data });
  };

  const navigateToEntity = (type, id) => {
    navigate(`/${type}/${id}`);
    setDetailModal(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Dashboard</h1>
        <p className="text-muted-foreground">Visão geral do estado de compliance</p>
      </div>

      {/* Stats Grid - Clickable */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div 
          onClick={() => handleCardClick('clients', entityStats?.clients)}
          className="stat-card cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
          data-testid="clients-stat-card"
        >
          <div className="flex items-center justify-between">
            <div className="p-3 rounded-lg text-blue-500 bg-blue-100 dark:bg-blue-900/30">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <span className="text-xs text-muted-foreground">Clique para detalhes</span>
          </div>
          <div className="mt-4">
            <p className="text-3xl font-bold font-['Plus_Jakarta_Sans']">{stats?.total_clients || 0}</p>
            <p className="text-sm text-muted-foreground">Total Clientes</p>
            {entityStats?.clients?.high_risk > 0 && (
              <p className="text-xs text-red-500 mt-1">{entityStats.clients.high_risk} alto risco</p>
            )}
          </div>
        </div>

        <div 
          onClick={() => handleCardClick('suppliers', entityStats?.suppliers)}
          className="stat-card cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
          data-testid="suppliers-stat-card"
        >
          <div className="flex items-center justify-between">
            <div className="p-3 rounded-lg text-purple-500 bg-purple-100 dark:bg-purple-900/30">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <span className="text-xs text-muted-foreground">Clique para detalhes</span>
          </div>
          <div className="mt-4">
            <p className="text-3xl font-bold font-['Plus_Jakarta_Sans']">{stats?.total_suppliers || 0}</p>
            <p className="text-sm text-muted-foreground">Total Fornecedores</p>
            {entityStats?.suppliers?.high_risk > 0 && (
              <p className="text-xs text-red-500 mt-1">{entityStats.suppliers.high_risk} alto risco</p>
            )}
          </div>
        </div>

        <div 
          onClick={() => handleCardClick('employees', entityStats?.employees)}
          className="stat-card cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
          data-testid="employees-stat-card"
        >
          <div className="flex items-center justify-between">
            <div className="p-3 rounded-lg text-emerald-500 bg-emerald-100 dark:bg-emerald-900/30">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <span className="text-xs text-muted-foreground">Clique para detalhes</span>
          </div>
          <div className="mt-4">
            <p className="text-3xl font-bold font-['Plus_Jakarta_Sans']">{stats?.total_employees || 0}</p>
            <p className="text-sm text-muted-foreground">Total Colaboradores</p>
          </div>
        </div>

        <div 
          onClick={() => navigate('/conflicts')}
          className={`stat-card cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all ${stats?.active_conflicts > 0 ? 'ring-2 ring-red-500' : ''}`}
          data-testid="conflicts-stat-card"
        >
          <div className="flex items-center justify-between">
            <div className="p-3 rounded-lg text-red-500 bg-red-100 dark:bg-red-900/30">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>
          <div className="mt-4">
            <p className="text-3xl font-bold font-['Plus_Jakarta_Sans']">{stats?.active_conflicts || 0}</p>
            <p className="text-sm text-muted-foreground">Conflitos Ativos</p>
          </div>
        </div>
      </div>

      {/* Offer Analytics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Offer Trend Chart */}
        <div className="lg:col-span-2 rounded-xl border bg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold font-['Plus_Jakarta_Sans']">Tendência de Ofertas</h2>
              <p className="text-sm text-muted-foreground">Evolução das ofertas recebidas</p>
            </div>
            <div 
              onClick={() => navigate('/offers')}
              className="text-sm text-primary cursor-pointer hover:underline"
            >
              Ver todas →
            </div>
          </div>
          
          {offerAnalytics?.trend_data?.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={offerAnalytics.trend_data}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getDate()}/${date.getMonth() + 1}`;
                  }}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                  labelFormatter={(value) => `Data: ${value}`}
                  formatter={(value, name) => {
                    const labels = { total: 'Total', value: 'Valor (€)', approved: 'Aprovadas', rejected: 'Rejeitadas', exceeds_limit: 'Excede Limite' };
                    return [value, labels[name] || name];
                  }}
                />
                <Legend />
                <Line type="monotone" dataKey="total" stroke="#3b82f6" strokeWidth={2} name="Total" dot={{ r: 4 }} />
                <Line type="monotone" dataKey="approved" stroke="#10b981" strokeWidth={2} name="Aprovadas" dot={{ r: 3 }} />
                <Line type="monotone" dataKey="rejected" stroke="#ef4444" strokeWidth={2} name="Rejeitadas" dot={{ r: 3 }} />
                <Line type="monotone" dataKey="exceeds_limit" stroke="#f59e0b" strokeWidth={2} name="Excede Limite" dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-muted-foreground">
              <p>Sem dados de ofertas para mostrar</p>
            </div>
          )}
        </div>

        {/* Summary Stats */}
        <div className="space-y-4">
          <div className="rounded-xl border bg-card p-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-3">Resumo de Ofertas</h3>
            <div className="space-y-2">
              <div 
                className="flex justify-between items-center p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => navigate('/offers')}
                data-testid="summary-total-offers"
              >
                <span className="text-sm">Total de Ofertas</span>
                <span className="font-bold">{offerAnalytics?.summary?.total_offers || 0}</span>
              </div>
              <div 
                className="flex justify-between items-center p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => navigate('/offers')}
                data-testid="summary-total-value"
              >
                <span className="text-sm">Valor Total</span>
                <span className="font-bold text-emerald-500">{offerAnalytics?.summary?.total_value?.toLocaleString('pt-PT') || 0} €</span>
              </div>
              <div 
                className="flex justify-between items-center p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => navigate('/offers')}
                data-testid="summary-avg-value"
              >
                <span className="text-sm">Valor Médio</span>
                <span className="font-bold">{offerAnalytics?.summary?.avg_value?.toFixed(2) || 0} €</span>
              </div>
              <div 
                className="flex justify-between items-center p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => handleCardClick('exceeds_limit', offerAnalytics?.exceeds_limit_offers)}
                data-testid="summary-exceeds-limit"
              >
                <span className="text-sm">Excedem Limite</span>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-orange-500">{offerAnalytics?.summary?.exceeds_limit_count || 0}</span>
                  <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
              <div 
                className="flex justify-between items-center p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => handleCardClick('auto_rejected', offerAnalytics?.auto_rejected_offers)}
                data-testid="summary-auto-rejected"
              >
                <span className="text-sm">Auto-Rejeitadas</span>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-red-500">{offerAnalytics?.summary?.auto_rejected_count || 0}</span>
                  <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
              <div 
                className="flex justify-between items-center p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => handleCardClick('pending', offerAnalytics?.pending_offers)}
                data-testid="summary-pending"
              >
                <span className="text-sm">Pendentes de Aprovação</span>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-amber-500">{offerAnalytics?.summary?.pending_count || 0}</span>
                  <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Top Offerers */}
          <div className="rounded-xl border bg-card p-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-3">Principais Ofertantes</h3>
            <div className="space-y-2">
              {offerAnalytics?.top_offerers?.slice(0, 5).map((offerer, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => handleCardClick('offerer', offerer)}
                  data-testid={`top-offerer-${idx}`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${
                      offerer.type === 'client' ? 'bg-blue-500' : 
                      offerer.type === 'supplier' ? 'bg-purple-500' : 'bg-gray-500'
                    }`}></span>
                    <span className="text-sm truncate max-w-32">{offerer.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{offerer.value?.toLocaleString('pt-PT')} €</span>
                    <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              ))}
              {(!offerAnalytics?.top_offerers || offerAnalytics.top_offerers.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-2">Sem dados</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Offers by Type and Source Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* By Type */}
        <div className="rounded-xl border bg-card p-6">
          <h2 className="text-lg font-semibold font-['Plus_Jakarta_Sans'] mb-4">Ofertas por Tipo</h2>
          {offerAnalytics?.by_type?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={offerAnalytics.by_type}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="type" 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => TYPE_LABELS[value] || value}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                  labelFormatter={(value) => TYPE_LABELS[value] || value}
                />
                <Bar dataKey="count" fill="#3b82f6" name="Quantidade" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-muted-foreground">
              <p>Sem dados</p>
            </div>
          )}
        </div>

        {/* By Source */}
        <div className="rounded-xl border bg-card p-6">
          <h2 className="text-lg font-semibold font-['Plus_Jakarta_Sans'] mb-4">Ofertas por Origem</h2>
          {offerAnalytics?.by_source?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={offerAnalytics.by_source}
                  dataKey="value"
                  nameKey="source"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ source, percent }) => `${SOURCE_LABELS[source] || source}: ${(percent * 100).toFixed(0)}%`}
                >
                  {offerAnalytics.by_source.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                  formatter={(value, name) => [`${value.toLocaleString('pt-PT')} €`, SOURCE_LABELS[name] || name]}
                />
                <Legend formatter={(value) => SOURCE_LABELS[value] || value} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-muted-foreground">
              <p>Sem dados</p>
            </div>
          )}
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div 
          onClick={() => navigate('/offers')}
          className="stat-card cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
        >
          <div className="flex items-center justify-between">
            <div className="p-3 rounded-lg text-amber-500 bg-amber-100 dark:bg-amber-900/30">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
              </svg>
            </div>
          </div>
          <div className="mt-4">
            <p className="text-3xl font-bold font-['Plus_Jakarta_Sans']">{stats?.pending_offers || 0}</p>
            <p className="text-sm text-muted-foreground">Ofertas Pendentes</p>
          </div>
        </div>
        <StatCard
          title="Entidades Alto Risco"
          value={stats?.high_risk_entities || 0}
          icon="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          color="red"
        />
        <StatCard
          title="Documentos a Expirar"
          value={stats?.expiring_documents || 0}
          icon="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          color="amber"
        />
      </div>

      {/* RGPC Compliance & Risk Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* RGPC Compliance */}
        <div className="rounded-xl border bg-card p-6">
          <h2 className="text-lg font-semibold font-['Plus_Jakarta_Sans'] mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Conformidade RGPC
          </h2>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                <p className="text-2xl font-bold text-emerald-600">{stats?.rgpc_compliant_offers || 0}</p>
                <p className="text-xs text-muted-foreground">Conformes</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-red-50 dark:bg-red-900/20">
                <p className="text-2xl font-bold text-red-600">{stats?.rgpc_non_compliant_offers || 0}</p>
                <p className="text-xs text-muted-foreground">Não Conformes</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                <p className="text-2xl font-bold text-gray-600">{stats?.rgpc_pending_analysis || 0}</p>
                <p className="text-xs text-muted-foreground">Pendentes</p>
              </div>
            </div>
            {(stats?.rgpc_compliant_offers > 0 || stats?.rgpc_non_compliant_offers > 0) && (
              <div className="h-2 rounded-full bg-muted overflow-hidden flex">
                <div 
                  className="bg-emerald-500 h-full" 
                  style={{ 
                    width: `${((stats?.rgpc_compliant_offers || 0) / ((stats?.rgpc_compliant_offers || 0) + (stats?.rgpc_non_compliant_offers || 0))) * 100}%` 
                  }}
                ></div>
                <div 
                  className="bg-red-500 h-full" 
                  style={{ 
                    width: `${((stats?.rgpc_non_compliant_offers || 0) / ((stats?.rgpc_compliant_offers || 0) + (stats?.rgpc_non_compliant_offers || 0))) * 100}%` 
                  }}
                ></div>
              </div>
            )}
            <button
              onClick={() => navigate('/offers')}
              className="w-full py-2 text-sm text-primary hover:bg-muted rounded-lg transition-colors"
            >
              Ver Ofertas →
            </button>
          </div>
        </div>

        {/* NIF Cross-Reference & Risk Alerts */}
        <div className="rounded-xl border bg-card p-6">
          <h2 className="text-lg font-semibold font-['Plus_Jakarta_Sans'] mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Alertas de Risco
          </h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                </svg>
                <span className="font-medium">Conflitos por NIF</span>
              </div>
              <span className="text-xl font-bold text-amber-600">{stats?.nif_based_conflicts || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">Riscos de Ofertas</span>
              </div>
              <span className="text-xl font-bold text-red-600">{stats?.offer_risk_conflicts || 0}</span>
            </div>
            {(stats?.nif_based_conflicts > 0 || stats?.offer_risk_conflicts > 0) && (
              <p className="text-xs text-muted-foreground text-center mt-2">
                Clique em Conflitos para ver detalhes e visualização em grafo
              </p>
            )}
            <button
              onClick={() => navigate('/conflicts')}
              className="w-full py-2 text-sm text-primary hover:bg-muted rounded-lg transition-colors"
            >
              Ver Conflitos →
            </button>
          </div>
        </div>
      </div>

      {/* Recent Conflicts */}
      <div className="rounded-xl border bg-card p-6">
        <h2 className="text-lg font-semibold font-['Plus_Jakarta_Sans'] mb-4">Conflitos Recentes</h2>
        {stats?.recent_conflicts?.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">Nenhum conflito detetado</p>
        ) : (
          <div className="space-y-3">
            {stats?.recent_conflicts?.map((conflict) => (
              <div 
                key={conflict.conflict_id} 
                className="flex items-center justify-between p-4 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted transition-colors"
                onClick={() => navigate('/conflicts')}
              >
                <div>
                  <p className="font-medium">{conflict.entity1_name} ↔ {conflict.entity2_name}</p>
                  <p className="text-sm text-muted-foreground">{conflict.relationship}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  conflict.severity === 'high' || conflict.severity === 'critical' ? 'risk-badge-high' :
                  conflict.severity === 'medium' ? 'risk-badge-medium' : 'risk-badge-low'
                }`}>
                  {conflict.severity}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Real-Time Alerts Panel */}
      <RealTimeAlertsPanel />

      {/* Detail Modal */}
      {detailModal && (
        <DashboardDetailModal
          type={detailModal.type}
          data={detailModal.data}
          onClose={() => setDetailModal(null)}
          onNavigate={navigateToEntity}
        />
      )}
    </div>
  );
};

export default DashboardPage;
